import { useState, useEffect, useMemo, useCallback } from 'react';
import { ReferralService, sharedApiClient } from '../fetcher';
import { useNotifications } from '@medprompt/shared';

/**
 * The referral modal opened from the patient diary's pending referrals. Saving patches the
 * patient record in place through the given callbacks rather than refetching it.
 */
const usePendingReferralModal = ({
  loadKey,
  removePendingReferralLocally,
  applyPendingReferralPatch,
  dismissPendingReferrals,
}) => {
  const { addNotification } = useNotifications();
  const referralService = useMemo(() => new ReferralService(sharedApiClient), []);

  const [selectedReferral, setSelectedReferral] = useState(null);
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [referralModalLoading, setReferralModalLoading] = useState(false);

  useEffect(() => {
    setReferralModalOpen(false);
    setSelectedReferral(null);
  }, [loadKey]);

  const handleReferralClick = useCallback((referral) => {
    setSelectedReferral(referral);
    setReferralModalOpen(true);
    // Close the proactive modal if it was open (user clicked through)
    dismissPendingReferrals?.();
  }, [dismissPendingReferrals]);

  const closeReferralModal = useCallback(() => {
    setReferralModalOpen(false);
    setSelectedReferral(null);
  }, []);

  const handleReferralMarkComplete = useCallback(async (referralId) => {
    setReferralModalLoading(true);
    try {
      await referralService.completeReferral(referralId);
      removePendingReferralLocally(referralId);
      setReferralModalLoading(false);
      setReferralModalOpen(false);
      setSelectedReferral(null);
    } catch (error) {
      console.error('Failed to complete referral:', error);
      setReferralModalLoading(false);
      addNotification('referral-complete-error', 'Failed to mark referral as complete', { isError: true });
    }
  }, [referralService, removePendingReferralLocally, addNotification]);

  // Does not touch `selectedReferral`: ReferralModal resets its edit fields and clears edit mode
  // from the `referral` prop on identity change, which would drop the user out mid-edit.
  const handleReferralUpdate = useCallback(async (update) => {
    setReferralModalLoading(true);
    try {
      await referralService.updateReferral(update);
      const { referralId, ...fields } = update;
      applyPendingReferralPatch(referralId, fields);
      setReferralModalLoading(false);
    } catch (error) {
      console.error('Failed to update referral:', error);
      setReferralModalLoading(false);
      addNotification('referral-update-error', 'Failed to update referral', { isError: true });
      throw error;
    }
  }, [referralService, applyPendingReferralPatch, addNotification]);

  const handleReferralDelete = useCallback(async (referralId) => {
    setReferralModalLoading(true);
    try {
      await referralService.deleteReferral(referralId);
      removePendingReferralLocally(referralId);
      setReferralModalLoading(false);
      setReferralModalOpen(false);
      setSelectedReferral(null);
    } catch (error) {
      console.error('Failed to delete referral:', error);
      setReferralModalLoading(false);
      addNotification('referral-delete-error', 'Failed to delete referral', { isError: true });
    }
  }, [referralService, removePendingReferralLocally, addNotification]);

  return {
    selectedReferral,
    referralModalOpen,
    referralModalLoading,
    handleReferralClick,
    closeReferralModal,
    handleReferralMarkComplete,
    handleReferralUpdate,
    handleReferralDelete,
  };
};

export default usePendingReferralModal;
